import { Component, ChangeDetectionStrategy, inject, input } from '@angular/core';
import {
	MatTableDataSource,
	MatTable,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
	MatNoDataRow,
} from '@angular/material/table';
import { SimpleRealEstate } from '../../../util/simpleEntities/SimpleRealEstate';
import { TenantUnit } from '@eadsysteme/ewsrealestateclient-v1';
import { Consumption, ConsumptionPeriod, DeviceConsumption, DeviceConsumptionSummary, EnergyBaseCalculation } from '@eadsysteme/energyefficiencyclient-v2';
import { SessionService } from '../../../services/session.service';
import { Icons } from '../../../util/Icons';
import { MatDialog } from '@angular/material/dialog';
import { LayoutService } from '../../../../@vex/services/layout.service';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { ReadingValueInfoDialogComponent } from './reading-value-info-dialog/reading-value-info-dialog.component';
import { CalculationInfoDialogComponent } from './calculation-info-dialog/calculation-info-dialog.component';
import { fadeInUp400ms } from '../../../../@vex/animations/fade-in-up.animation';
import { stagger40ms } from '../../../../@vex/animations/stagger.animation';
import { NumberUtil } from '../../../util/NumberUtil';
import { TranslocoLocaleService } from '@jsverse/transloco-locale';
import { DateUtil } from '../../../util/DateUtil';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { DefaultClassDirective } from '@ngbracket/ngx-layout/extended';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import DeviceTypeEnum = DeviceConsumption.DeviceTypeEnum;
import CostTypeEnum = Consumption.CostTypeEnum;
import ConsumptionUnitEnum = DeviceConsumptionSummary.ConsumptionUnitEnum;

@Component({
	selector: 'vex-percent-display-per-energytype',
	templateUrl: './percent-display-per-energy-type.component.html',
	styleUrl: './percent-display-per-energy-type.component.scss',
	animations: [fadeInUp400ms, stagger40ms],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		TranslocoDirective,
		MatTable,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatCellDef,
		MatCell,
		NgClass,
		DefaultClassDirective,
		MatIconButton,
		MatIcon,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		MatNoDataRow,
		NgTemplateOutlet,
	],
})
export class PercentDisplayPerEnergyTypeComponent {
	sessionService = inject(SessionService);
	icons = inject(Icons);
	dateUtil = inject(DateUtil);
	numberUtil = inject(NumberUtil);
	layoutService = inject(LayoutService);
	private dialog = inject(MatDialog);
	private transloco = inject(TranslocoService);
	private translocoLocale = inject(TranslocoLocaleService);

	dataSource: MatTableDataSource<DeviceConsumption>;
	displayedColumns: string[] = [
		'roomType',
		'deviceNumber',
		'transmitterNumber',
		'deviceType',
		'periodBeginValue',
		'periodEndValue',
		'factor',
		'consumption',
		'chart',
		'percentShare',
		'information',
	];
	readonly realEstate = input<SimpleRealEstate>(undefined);
	readonly tenantUnit = input<TenantUnit>(undefined);
	finishedLoading: boolean;
	readonly consumption = input<Consumption>(undefined);
	protected readonly CostTypeEnum = CostTypeEnum;
	protected readonly DeviceTypeEnum = DeviceTypeEnum;
	protected anyDeviceNotTransmitted = false;

	constructor() {
		this.numberUtil._locale = this.translocoLocale.getLocale();
	}

	readingDateBegin(consumptionPeriod: ConsumptionPeriod) {
		return this.dateUtil.setDateFormatByActiveLang(
			consumptionPeriod?.deviceConsumptions[0]?.energyBaseCalculation?.periodBeginEstimatedReadingValue?.readingDate,
			''
		);
	}

	readingDateEnd(consumptionPeriod: ConsumptionPeriod) {
		return this.dateUtil.setDateFormatByActiveLang(
			consumptionPeriod?.deviceConsumptions[0]?.energyBaseCalculation?.periodEndEstimatedReadingValue?.readingDate,
			''
		);
	}

	showReadingValueInfo(energyBaseCalculation: EnergyBaseCalculation, deviceType: string, deviceNumber: string) {
		this.dialog.open(ReadingValueInfoDialogComponent, {
			width: '500px',
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
			data: [energyBaseCalculation, deviceType, deviceNumber, this.consumption().costType],
			ariaLabel: deviceType,
		});
	}

	showCalculationInfo(summedEnergy: ConsumptionPeriod) {
		this.dialog.open(CalculationInfoDialogComponent, {
			width: '500px',
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
			data: summedEnergy.deviceConsumptionSummaries,
		});
	}

	toDataSource(deviceConsumption: DeviceConsumption[]) {
		this.anyDeviceNotTransmitted =
			deviceConsumption.filter((value) => {
				return !(value.consumption > -1); // flipped the condition so that consumption===undefined returns true
			}).length > 0;
		return new MatTableDataSource(deviceConsumption);
	}

	withUnit(deviceConsumptionSummary: DeviceConsumptionSummary): string {
		return (
			this.numberUtil.scaleAndReplaceDot(deviceConsumptionSummary.consumption, 2) +
			' ' +
			this.transloco.translate('enum.' + (deviceConsumptionSummary.consumptionUnit ?? ConsumptionUnitEnum.UNITS))
		);
	}

	appendKwh(consumption: number) {
		return this.numberUtil.scaleAndReplaceDot(consumption) + ' ' + this.transloco.translate('enum.KWH');
	}
}
